import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Stack,
  CircularProgress,
  Button,
  Tooltip,
  Chip,
  Alert,
  Badge,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { 
  Send as SendIcon, 
  AttachFile as AttachFileIcon, 
  Close as CloseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  ThumbUpOffAlt as ThumbUpOffAltIcon,
  ThumbDownOffAlt as ThumbDownOffAltIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import CONVERSAYION_MESSAGE_TYPES from '../constants/conversationMessageTypes';
import { DEMOGRAPHIC_CHART_COLORS } from '../constants/property';
import InlineVoiceInput from './InlineVoiceInput';
import SparkleIcon from './SparkleIcon';
import AiMarkdownRenderer from './markdown/AiMarkdownRenderer';
import KpiTiles from './chat/KpiTiles';
import ConfirmActionCard from './chat/ConfirmActionCard';
import ArtifactCards from './chat/ArtifactCards';
import ChatSectionDivider from './chat/ChatSectionDivider';
import { isConfirmationAction, pendingOutcomes } from '../utils/chatStreamReducer';
import { formatMeasure } from '../utils/measure';
import { uploadDocumentTemporarily, deleteTemporaryDocument } from '../services/chatService';
import { speechQueue } from '../services/ttsService';
import { CHAT_QUERY_MAX_CHARS } from '../hooks/useChat';
import { analytics } from '../utils/analytics';
import { formatDateTime, formatTime } from '../utils/dateUtils';
import { useNotification } from '../contexts/NotificationContext';

const toFiniteNumber = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }
  return numericValue;
};

const formatDemographicValue = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric.toLocaleString();
  }
  return String(value);
};

const formatDemographicPercent = (value, total) => {
  const numericValue = toFiniteNumber(value);
  const numericTotal = toFiniteNumber(total);

  if (numericValue === null || numericTotal === null || numericTotal <= 0) {
    return '0.0%';
  }

  const percent = (numericValue / numericTotal) * 100;
  if (percent > 0 && percent < 0.1) {
    return '<0.1%';
  }

  return `${percent.toFixed(1)}%`;
};

// Message time in the browser's timezone; messages from earlier days also show the date.
const formatMessageTimestamp = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;

  // en-US for "3:15 PM" (en-CA renders "3:15 p.m.").
  if (date.toDateString() === new Date().toDateString()) {
    return formatTime(date, { locale: 'en-US' });
  }
  return formatDateTime(date, {
    locale: 'en-US',
    format: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
  });
};

/**
 * A React key per message that survives the list changing around it (an outcome line put in
 * under its card): the client's own key, else the server id, else the index.
 */
const messageKeys = (conversation) => {
  const seen = new Set();
  return conversation.map((msg, idx) => {
    let key = msg?.clientKey || (msg?.id ? `id-${msg.id}` : `idx-${idx}`);
    if (seen.has(key)) key = `${key}-${idx}`;
    seen.add(key);
    return key;
  });
};

// Animations
const pulse = keyframes`
  0% { 
    transform: scale(1);
    opacity: 1;
  }
  50% { 
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% { 
    transform: scale(1);
    opacity: 1;
  }
`;

/**
 * Helper function to detect file attachments in text
 */
const detectFileAttachment = (text) => {
  // Handle new format: 📎 file_path|original_filename or legacy format: 📎 filename
  const filePattern = /📎\s*([^|]+)(?:\|([^|]+))?/;
  // console.log("Detecting file attachment in text:", text);
  const match = text.match(filePattern);
  // console.log("File attachment match:", match);
  if (match) {
    const fileInfo = match[1].trim();
    let originalFilename = match[2] ? match[2].trim() : fileInfo;
    if (originalFilename) {
      // Extract only the first line of originalFilename to avoid custom prompts
      originalFilename = originalFilename.split('\n')[0].trim();
    }

    // For display, use the original filename if available, otherwise extract filename from path    
    let displayFilename = originalFilename;
    if (fileInfo.includes('/') && !originalFilename) {
      // Extract filename from path
      displayFilename = fileInfo.split('/').pop();
    }
    
    return {
      hasAttachment: true,
      filename: displayFilename,
      beforeText: text.substring(0, match.index).trim(),
      afterText: text.substring(match.index + match[0].length).trim()
    };
  }
  return { hasAttachment: false, filename: null, beforeText: text, afterText: '' };
};

/**
 * Component to render file attachment badge
 */
const FileAttachmentBadge = ({ filename, withSparkle = false, size = 'small', isPanel = false, onRemove = null, removable = false }) => (
  <Box sx={{ 
    position: 'relative', 
    display: 'inline-flex', 
    alignItems: 'center',
    mr: 1,
    maxWidth: isPanel ? '450px' : '600px', // Much larger widths for better filename display
    minWidth: 0,
    flexShrink: 1 // Allow badge to shrink if needed
  }}>
    <Chip
      icon={<AttachFileIcon sx={{ fontSize: size === 'small' ? (isPanel ? 12 : 14) : 16 }} />}
      label={filename}
      size={size}
      variant="outlined"
      deleteIcon={removable ? <CloseIcon sx={{ fontSize: size === 'small' ? 16 : 18 }} /> : undefined}
      onDelete={removable && onRemove ? onRemove : undefined}
      sx={{
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        borderColor: 'primary.main',
        color: 'primary.main',
        fontWeight: 500,
        maxWidth: '100%',
        position: 'relative',
        '& .MuiChip-icon': {
          color: 'primary.main',
          flexShrink: 0 // Prevent icon from shrinking
        },
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: isPanel ? '400px' : '550px', // Slightly reduced to accommodate delete icon
          display: 'block',
          position: 'relative',
          fontSize: isPanel ? '0.75rem' : undefined
        },
        '& .MuiChip-deleteIcon': {
          color: 'primary.main',
          '&:hover': {
            color: 'error.main',
            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1)
          }
        }
      }}
    />
    {withSparkle && (
      <SparkleIcon
        sx={{
          position: 'absolute',
          top: '-8px',
          right: removable ? '16px' : '-8px', // Adjust position when delete icon is present
          fontSize: '16px',
          color: '#FFD700',
          filter: 'drop-shadow(0 0 4px rgba(255, 215, 0, 0.6))',
          animation: `${pulse} 2s infinite`
        }}
      />
    )}
  </Box>
);

/**
 * Reusable chat interface component
 * Used by both ChatPage and ChatPanel
 */
/**
 * Component to render text with file attachments
 */
const TextWithAttachments = ({ text, isUser = false, withSparkle = false, isPanel = false, isInputField = false, onRemoveFile = null }) => {
  const attachment = detectFileAttachment(text);
  
  if (!attachment.hasAttachment) {
    return <span style={{ fontFamily: 'inherit', fontSize: 'inherit' }}>{text}</span>;
  }
  
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      flexWrap: 'wrap', // Allow wrapping for long filenames
      gap: 0.5,
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'visible' // Allow content to be visible for full filename display
    }}>
      {attachment.beforeText && (
        <Typography component="span" sx={{ 
          mr: 1, 
          minWidth: 0,
          flexShrink: 1,
          fontFamily: 'inherit',
          fontSize: 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {attachment.beforeText}
        </Typography>
      )}
      <FileAttachmentBadge 
        filename={attachment.filename} 
        withSparkle={withSparkle}
        size={isUser ? 'small' : 'medium'}
        isPanel={isPanel}
        removable={isInputField}
        onRemove={isInputField ? onRemoveFile : undefined}
      />
      {attachment.afterText && (
        <Typography component="span" sx={{ 
          ml: 1, 
          minWidth: 0,
          flexShrink: 1,
          fontFamily: 'inherit',
          fontSize: 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {attachment.afterText}
        </Typography>
      )}
    </Box>
  );
};

const ChatVisualizations = ({ visualizations = [], isPanel = false }) => {
  const theme = useTheme();
  if (!Array.isArray(visualizations) || visualizations.length === 0) {
    return null;
  }

  const renderChart = (visualization, chartIndex) => {
    const chartType = String(visualization?.chart_type || '').toLowerCase();
    const xKey = String(visualization?.x_key || 'label');
    const yKey = String(visualization?.y_key || 'value');
    const title = String(visualization?.title || 'Chart');
    const description = typeof visualization?.description === 'string' ? visualization.description : null;
    const unit = typeof visualization?.unit === 'string' ? visualization.unit.trim() : '';
    const data = Array.isArray(visualization?.data)
      ? visualization.data.filter((item) => (
        item &&
        typeof item === 'object' &&
        typeof item.label === 'string' &&
        Number.isFinite(Number(item.value))
      )).map((item) => ({
        label: item.label,
        value: Number(item.value),
        unit: typeof item.unit === 'string' ? item.unit : undefined,
        change: typeof item.change === 'string' ? item.change : undefined,
      }))
      : [];

    if (data.length === 0) {
      return null;
    }

    if (chartType === 'kpi') {
      return (
        <KpiTiles
          key={`kpi_${chartIndex}_${title}`}
          title={title}
          description={description}
          items={data}
          isPanel={isPanel}
        />
      );
    }

    // A pie reads best largest-first; bars and lines keep the order given (a series must not be sorted).
    const ordered = chartType === 'pie' ? [...data].sort((a, b) => Number(b.value) - Number(a.value)) : data;
    const chartData = ordered.map((item) => ({
      ...item,
      [xKey]: item.label,
      [yKey]: item.value,
    }));

    const chartTotal = chartData.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const pieChartHeight = isPanel ? 250 : 310;
    const axisFontSize = isPanel ? 9 : 12;
    const pieLayout = isPanel
      ? {
        wrapperDirection: 'column',
        chartWidth: '100%',
        legendWidth: '100%',
        legendBorderLeft: 'none',
        legendPaddingLeft: 0,
        legendBorderTop: `1px solid ${theme.palette.divider}`,
        legendPaddingTop: 1.5,
        legendMaxHeight: 270,
      }
      : {
        wrapperDirection: { xs: 'column', md: 'row' },
        chartWidth: { xs: '100%', md: '66%' },
        legendWidth: { xs: '100%', md: '34%' },
        legendBorderLeft: { xs: 'none', md: `1px solid ${theme.palette.divider}` },
        legendPaddingLeft: { xs: 0, md: 2 },
        legendBorderTop: 'none',
        legendPaddingTop: 0,
        legendMaxHeight: { md: pieChartHeight },
      };

    return (
      <Paper
        key={`${chartType}_${chartIndex}_${title}`}
        elevation={0}
        sx={{
          mt: 1.5,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: theme.palette.modern.slate[50],
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: isPanel ? '0.95rem' : '1.05rem', mb: 0.25 }}>
          {title}
        </Typography>
        {description && (
          <Typography sx={{ color: 'text.secondary', fontSize: isPanel ? '0.8rem' : '0.875rem', mb: 0.5 }}>
            {description}
          </Typography>
        )}
        {unit && (
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0 }}>
            Values in {unit}
          </Typography>
        )}
        {chartType === 'pie' && (
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0 }}>
            Total shown in chart: {formatDemographicValue(chartTotal)}
          </Typography>
        )}

        {chartType === 'pie' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: pieLayout.wrapperDirection,
              alignItems: 'stretch',
              gap: 0,
            }}
          >
            <Box
              sx={{
                width: pieLayout.chartWidth,
                height: pieChartHeight,
                flexShrink: 0,
              }}
            >
              <ResponsiveContainer width="100%" height={pieChartHeight} minWidth={0}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey={yKey}
                    nameKey={xKey}
                    cx="50%"
                    cy="50%"
                    innerRadius="40%"
                    outerRadius="85%"
                    paddingAngle={2}
                    minAngle={2}
                  >
                    {chartData.map((entry, idx) => (
                      <Cell
                        key={`${entry.label}_${idx}`}
                        fill={DEMOGRAPHIC_CHART_COLORS[idx % DEMOGRAPHIC_CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value, name) => [
                      `${formatMeasure(value, unit)} (${formatDemographicPercent(value, chartTotal)})`,
                      name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <Box
              sx={{
                width: pieLayout.legendWidth,
                minWidth: 0,
                borderLeft: pieLayout.legendBorderLeft,
                pl: pieLayout.legendPaddingLeft,
                borderTop: pieLayout.legendBorderTop,
                pt: pieLayout.legendPaddingTop,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  maxHeight: pieLayout.legendMaxHeight,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                {chartData.map((item, idx) => (
                  <Box
                    key={`${item.label}_${idx}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 0.75,
                        flexShrink: 0,
                        backgroundColor: DEMOGRAPHIC_CHART_COLORS[idx % DEMOGRAPHIC_CHART_COLORS.length],
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: isPanel ? '0.875rem' : undefined,
                        whiteSpace: isPanel ? 'normal' : 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={item.label}
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatDemographicPercent(item.value, chartTotal)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : chartType === 'bar' ? (
          <Box sx={{ width: '100%', height: isPanel ? 220 : 280 }}>
            <ResponsiveContainer width="100%" height={isPanel ? 220 : 280} minWidth={0}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.modern.slate[200]} />
                <XAxis dataKey={xKey} tick={{ fontSize: axisFontSize }} />
                <YAxis tick={{ fontSize: axisFontSize }} />
                <RechartsTooltip formatter={(value, name) => [formatMeasure(value, unit), name]} />
                <Bar dataKey={yKey} fill={DEMOGRAPHIC_CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ width: '100%', height: isPanel ? 220 : 280 }}>
            <ResponsiveContainer width="100%" height={isPanel ? 220 : 280} minWidth={0}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.modern.slate[200]} />
                <XAxis dataKey={xKey} tick={{ fontSize: axisFontSize }} />
                <YAxis tick={{ fontSize: axisFontSize }} />
                <RechartsTooltip formatter={(value, name) => [formatMeasure(value, unit), name]} />
                <Line
                  type="monotone"
                  dataKey={yKey}
                  stroke={DEMOGRAPHIC_CHART_COLORS[0]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
    );
  };

  return <>{visualizations.map((visualization, index) => renderChart(visualization, index))}</>;
};

// Custom Waveform Icon for Talk Mode (ChatGPT style)
const WaveformIcon = ({ isActive, ...props }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <rect x="4" y="8" width="2" height="8" rx="1" opacity={isActive ? "1" : "0.8"}>
      {isActive && (
        <animate
          attributeName="height"
          values="8;12;8"
          dur="1s"
          repeatCount="indefinite"
        />
      )}
      {isActive && (
        <animate
          attributeName="y"
          values="8;6;8"
          dur="1s"
          repeatCount="indefinite"
        />
      )}
    </rect>
    <rect x="8" y="5" width="2" height="14" rx="1" opacity={isActive ? "1" : "0.8"}>
      {isActive && (
        <animate
          attributeName="height"
          values="14;16;14"
          dur="1s"
          begin="0.2s"
          repeatCount="indefinite"
        />
      )}
      {isActive && (
        <animate
          attributeName="y"
          values="5;4;5"
          dur="1s"
          begin="0.2s"
          repeatCount="indefinite"
        />
      )}
    </rect>
    <rect x="12" y="3" width="2" height="18" rx="1" opacity={isActive ? "1" : "0.8"}>
      {isActive && (
        <animate
          attributeName="height"
          values="18;20;18"
          dur="1s"
          begin="0.4s"
          repeatCount="indefinite"
        />
      )}
      {isActive && (
        <animate
          attributeName="y"
          values="3;2;3"
          dur="1s"
          begin="0.4s"
          repeatCount="indefinite"
        />
      )}
    </rect>
    <rect x="16" y="6" width="2" height="12" rx="1" opacity={isActive ? "1" : "0.8"}>
      {isActive && (
        <animate
          attributeName="height"
          values="12;15;12"
          dur="1s"
          begin="0.6s"
          repeatCount="indefinite"
        />
      )}
      {isActive && (
        <animate
          attributeName="y"
          values="6;4.5;6"
          dur="1s"
          begin="0.6s"
          repeatCount="indefinite"
        />
      )}
    </rect>
  </svg>
);

const MemoizedMessageList = React.memo(({
  conversation,
  isPanel,
  loading,
  showLoadingIndicator,
  isAiThinking,
  voiceMode,
  isSpeaking,
  isVoiceListening,
  messagesEndRef,
  messageContainerSx,
  messageSx,
  onSubmitFeedback,
  feedbackSubmittingId,
}) => {
  const [hoveredMessageKey, setHoveredMessageKey] = useState(null);
  // Outcome lines of confirmed actions, by pending id, for the confirm cards above them.
  const outcomes = React.useMemo(() => pendingOutcomes(conversation), [conversation]);
  const keys = React.useMemo(() => messageKeys(conversation), [conversation]);

  return (
    <Box sx={{ 
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden', // Prevent horizontal scroll
      minHeight: 0, // Important for flex scrolling
      height: '100%', // Ensure full height usage
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center', // Center the message container
      position: 'relative',
      // Custom scrollbar styling
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: (theme) => alpha(theme.palette.text.primary, 0.2),
        borderRadius: '3px',
        '&:hover': {
          background: (theme) => alpha(theme.palette.text.primary, 0.3),
        }
      },
      ...messageContainerSx
    }}>
      <Box sx={{ 
        width: '100%',
        maxWidth: isPanel ? '100%' : '1000px', // Constrain message area width
        mx: 'auto',
        pt: 1, // Add padding to account for fade effect
        pb: 0, // Add padding to account for fade effect
        pr: 0.5
      }}>
        {conversation.map((msg, idx) => {
          const key = keys[idx];

          // Check if this message has a separator label (MLS assistance)
          const hasSeparator = msg.separatorLabel && msg.requiresSeparator;

          // Check if previous message was also MLS assistance for different field
          const prevMsg = idx > 0 ? conversation[idx - 1] : null;
          const showSeparator = hasSeparator && (
            idx === 0 || // First message
            !prevMsg || // No previous message
            !prevMsg.separatorLabel || // Previous didn't have separator
            prevMsg.separatorLabel !== msg.separatorLabel // Different separator label
          );
          const sentAtLabel = msg.type === 'user' ? formatMessageTimestamp(msg.created_at) : null;
          // An answer with a row of several artifact cards gives the row the whole width; its
          // text keeps the usual measure, so it doesn't reflow when the cards arrive.
          const artifactRow = msg.type === 'ai' && Array.isArray(msg.artifacts) && msg.artifacts.length > 1;

          return (
            <React.Fragment key={key}>
              {/* Add separator for new field help sections */}
              {showSeparator && (
                <ChatSectionDivider
                  label={msg.separatorLabel}
                  icon={<EditNoteOutlinedIcon sx={{ fontSize: 16 }} />}
                />
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                  mb: sentAtLabel ? 0 : isPanel ? 1.5 : 2,
                  width: '100%' // Full width of message container
                }}
                onMouseEnter={() => setHoveredMessageKey(key)}
                onMouseLeave={() => setHoveredMessageKey(null)}
              >
                <Paper 
                  elevation={0} 
                  sx={{ 
                    ...messageSx,
                    ...(artifactRow && { width: '100%', maxWidth: '100%' }),
                    bgcolor: msg.type === 'user'
                      ? (theme) => alpha(theme.palette.primary.main, 0.1)
                      : msg.message_type === CONVERSAYION_MESSAGE_TYPES.ERROR
                        ? (theme) => alpha(theme.palette.error.main, 0.08)
                        : 'transparent',
                    borderRadius: 2,
                    borderBottomRightRadius: msg.type === 'user' ? 0 : 2,
                    ...(msg.message_type === CONVERSAYION_MESSAGE_TYPES.AI_THINKING && {
                      border: 'none',
                      background: 'none',
                      position: 'relative',
                    }),
                    ...(msg.message_type === CONVERSAYION_MESSAGE_TYPES.ERROR && {
                      border: '0px solid',
                      borderColor: 'error.main',
                      background: 'none',
                      position: 'relative',
                      pl: 2
                    })
                  }}
                >
                  {msg.type === 'user' ? (
                    <Box sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: isPanel ? '0.875rem' : undefined
                    }}>
                      <TextWithAttachments 
                        text={msg.text} 
                        isUser={true} 
                        withSparkle={false}
                        isPanel={isPanel}
                      />
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ maxWidth: artifactRow ? messageSx.maxWidth : undefined }}>
                        <AiMarkdownRenderer
                          content={msg.text || (msg.message_type === CONVERSAYION_MESSAGE_TYPES.AI_THINKING ? 'AI is thinking' : '')}
                          isPanel={isPanel}
                          isThinking={msg.message_type === CONVERSAYION_MESSAGE_TYPES.AI_THINKING}
                          isError={msg.message_type === CONVERSAYION_MESSAGE_TYPES.ERROR}
                          renderActionLinksAsButtons
                        />
                        {msg.message_type !== CONVERSAYION_MESSAGE_TYPES.AI_THINKING &&
                          msg.message_type !== CONVERSAYION_MESSAGE_TYPES.ERROR &&
                          Array.isArray(msg.visualizations) &&
                          msg.visualizations.length > 0 && (
                            <ChatVisualizations visualizations={msg.visualizations} isPanel={isPanel} />
                          )}
                        {msg.message_type !== CONVERSAYION_MESSAGE_TYPES.AI_THINKING &&
                          msg.message_type !== CONVERSAYION_MESSAGE_TYPES.AI_STREAMING &&
                          msg.message_type !== CONVERSAYION_MESSAGE_TYPES.ERROR &&
                          isConfirmationAction(msg.action) && (
                            <ConfirmActionCard
                              action={msg.action}
                              outcome={outcomes[msg.action.pending_id] || null}
                              confirmation={msg.confirmation || null}
                            />
                          )}
                      </Box>
                      {msg.message_type !== CONVERSAYION_MESSAGE_TYPES.AI_THINKING &&
                        msg.message_type !== CONVERSAYION_MESSAGE_TYPES.ERROR &&
                        Array.isArray(msg.artifacts) &&
                        msg.artifacts.length > 0 && (
                          <ArtifactCards artifacts={msg.artifacts} compact={isPanel} />
                        )}
                    </>
                  )}

                  {msg.type === 'ai' &&
                    msg.message_type !== CONVERSAYION_MESSAGE_TYPES.AI_THINKING &&
                    msg.message_type !== CONVERSAYION_MESSAGE_TYPES.AI_STREAMING &&
                    msg.message_type !== CONVERSAYION_MESSAGE_TYPES.ERROR &&
                    msg.id && (
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'flex-start',
                          gap: 0.5,
                          opacity: hoveredMessageKey === key || !!msg.feedback_value ? 1 : 0,
                          transition: 'opacity 0.15s ease',
                        }}
                      >
                        <Tooltip title="Helpful">
                          <span>
                            <IconButton
                              size="medium"
                              disabled={feedbackSubmittingId === msg.id}
                              onClick={() => onSubmitFeedback?.(msg, 'up')}
                              sx={{
                                color: msg.feedback_value === 'up' ? 'success.main' : 'text.secondary',
                              }}
                            >
                              <ThumbUpOffAltIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Not Helpful">
                          <span>
                            <IconButton
                              size="medium"
                              disabled={feedbackSubmittingId === msg.id}
                              onClick={() => onSubmitFeedback?.(msg, 'down')}
                              sx={{
                                color: msg.feedback_value === 'down' ? 'error.main' : 'text.secondary',
                              }}
                            >
                              <ThumbDownOffAltIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                </Paper>
              </Box>
              {sentAtLabel && (
                <Box
                  component="time"
                  dateTime={msg.created_at}
                  sx={{
                    display: 'flex',
                    fontSize: '0.65rem',
                    justifyContent: 'flex-end',
                    mb: isPanel ? 1.5 : 2,
                  }}>
                  {sentAtLabel}
                </Box>
              )}
            </React.Fragment>
          );
        })}
        
        {/* Loading indicator */}
        {loading && showLoadingIndicator && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        {/* AI thinking indicator (for field help and chat loading) */}
        {isAiThinking && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            my: 2,
          }}>
            <SparkleIcon
              size={24}
              animate="rotateContinuous"
            />
          </Box>
        )}

        {/* Voice mode status indicators */}
        {voiceMode && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: 1,
            my: 1,
          }}>
            {/* AI speaking indicator */}
            {isSpeaking && (
              <Chip
                icon={<VolumeUpIcon />}
                label="AI Speaking..."
                size="small"
                color="success"
                sx={{
                  animation: `${pulse} 1.5s ease-in-out infinite`,
                  fontWeight: 500
                }}
              />
            )}
            
            {/* Listening indicator - minimal pulsing circle */}
            {!isSpeaking && isVoiceListening && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    animation: `${pulse} 1.5s ease-in-out infinite`,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Listening...
                </Typography>
              </Box>
            )}
            
            {/* Waiting to listen */}
            {!isSpeaking && !isVoiceListening && loading && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Processing...
              </Typography>
            )}
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
});

const ChatInterface = ({
  conversation,
  query,
  setQuery,
  loading,
  isAiThinking = false,
  onSendMessage,
  onSubmitFeedback,
  feedbackSubmittingId = null,
  messagesEndRef,
  variant = 'fullscreen', // 'fullscreen' or 'panel'
  placeholder = "What would you like to know about real estate?",
  showLoadingIndicator = true,
  tokenLimitReached = false,
}) => {
  const isPanel = variant === 'panel';
  const { showNotification } = useNotification();
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [voiceStartQuery, setVoiceStartQuery] = useState('');
  
  // Voice mode states
  const [voiceMode, setVoiceMode] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoListenAfterSpeech, setAutoListenAfterSpeech] = useState(false);
  const [handsFreeModeActive, setHandsFreeModeActive] = useState(false);
  const [micPermissionError, setMicPermissionError] = useState(null);
  
  // File upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);
  const [uploadedFileData, setUploadedFileData] = useState(null); // Store uploaded file data
  const fileInputRef = useRef(null);
  const prevConversationLengthRef = useRef(0);
  const silenceTimerRef = useRef(null);
  const messageSentRef = useRef(false); // Prevent duplicate sends
  const currentAudioRef = useRef(null); // Track current playing audio for interruption

  /**
   * Interrupt any currently playing audio (TTS or backend audio)
   * Called when user starts speaking to stop AI response
   */
  const interruptCurrentAudio = (reason = 'User started speaking') => {
    console.log(`🛑 === INTERRUPTION ATTEMPT ===`);
    console.log(`🛑 Reason: ${reason}`);
    console.log(`🛑 Current state:`, {
      isSpeaking,
      speechQueuePlaying: speechQueue.getIsPlaying(),
      hasCurrentAudio: !!currentAudioRef.current,
      voiceMode,
      handsFreeModeActive
    });
    
    let wasPlaying = false;
    
    // Stop speech queue (frontend TTS)
    if (speechQueue.getIsPlaying()) {
      console.log('🔇 Stopping speechQueue...');
      speechQueue.interrupt(reason);
      wasPlaying = true;
    } else {
      console.log('⚠️ SpeechQueue not playing');
    }
    
    // Stop direct audio (backend TTS)
    if (currentAudioRef.current) {
      console.log('🔇 Stopping current backend audio');
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
      wasPlaying = true;
    } else {
      console.log('⚠️ No current backend audio');
    }
    
    // Update speaking state
    if (wasPlaying) {
      setIsSpeaking(false);
      console.log('✅ Audio interruption complete - set isSpeaking to false');
    } else {
      console.log('⚠️ No audio was playing to interrupt');
    }
    
    console.log(`🛑 === INTERRUPTION COMPLETE ===`);
    return wasPlaying;
  };
  
  /**
   * Handle voice input transcript
   */
  /**
   * Handle voice transcript from speech recognition
   * TALK MODE: User speaks → Show in text → 2s silence → Send + Clear → TTS → Listen again
   */
  const handleVoiceTranscript = (transcript, isFinal = true) => {
    console.log('========================================');
    console.log('🎤 Talk Mode: Voice transcript received');
    console.log('Transcript:', transcript.substring(0, 50));
    console.log('isFinal:', isFinal);
    console.log('Talk Mode Active:', handsFreeModeActive);
    console.log('========================================');
    
    // 🛑 INTERRUPT AUDIO IMMEDIATELY when user starts speaking
    if (transcript.trim() && handsFreeModeActive && (isSpeaking || speechQueue.getIsPlaying())) {
      console.log('🗣️ === USER SPEECH DETECTED ===');
      console.log('🗣️ Transcript:', transcript.substring(0, 30));
      console.log('🗣️ Conditions:', {
        hasTranscript: !!transcript.trim(),
        handsFreeModeActive,
        isSpeaking,
        speechQueuePlaying: speechQueue.getIsPlaying()
      });
      console.log('🗣️ CALLING interruptCurrentAudio...');
      interruptCurrentAudio('User started speaking during transcript');
    } else {
      console.log('🤐 No interruption needed:', {
        hasTranscript: !!transcript.trim(),
        handsFreeModeActive,
        isSpeaking,
        speechQueuePlaying: speechQueue.getIsPlaying()
      });
    }
    
    if (transcript.trim()) {
      // Always update the query with latest transcript
      const newQuery = voiceStartQuery.trim() 
        ? `${voiceStartQuery} ${transcript}` 
        : transcript;
      
      console.log('🎤 Setting query from voice:', newQuery.substring(0, 30));
      setQuery(newQuery);
      
      // In hands-free mode, set up auto-send timer on ANY transcript update
      if (handsFreeModeActive && newQuery.trim()) {
        console.log('✅ Conditions met for timer! hands-free:', handsFreeModeActive, 'hasQuery:', !!newQuery.trim());
        
        // Clear existing timer
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          console.log('⏰ TIMER RESET - user still speaking');
        }
        
        // If isFinal, send immediately; otherwise set timer
        if (isFinal) {
          console.log('✅ FINAL - sending immediately with message:', newQuery.substring(0, 50));
          
          // Check if we already sent this message
          if (messageSentRef.current) {
            console.log('⚠️ DUPLICATE PREVENTED - message already sent');
            setQuery('');
            return;
          }
          
          // IMPORTANT: Clear any existing timer to prevent duplicate send
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
            console.log('⏰ TIMER CLEARED - sending on isFinal instead');
          }
          
          setVoiceStartQuery('');
          setIsVoiceListening(false);
          
          // Mark as sent to prevent duplicates
          messageSentRef.current = true;
          
          // Send message first - PASS TALK MODE FLAG
          console.log('🎤 ChatInterface: Calling onSendMessage with talkMode=true (isFinal)');
          onSendMessage(null, false, newQuery, true);  // talkMode = true
          
          // Force clear immediately - multiple methods to ensure it works
          setQuery('');
          
          // Force clear via direct DOM manipulation as backup
          const textField = document.querySelector('input[type="text"], textarea');
          if (textField) {
            textField.value = '';
            // Trigger React's onChange to sync state
            const event = new Event('input', { bubbles: true });
            textField.dispatchEvent(event);
          }
          
          console.log('🧹 FORCED text field clear (isFinal)');
          
          // Additional backup clear
          setTimeout(() => {
            setQuery('');
            console.log('🧹 Backup text field clear (isFinal)');
          }, 0);
          
          return; // Don't set timer
        }
        
        // Interim result - set timer
        console.log('⏲️ Setting 2s timer');
        silenceTimerRef.current = setTimeout(() => {
          console.log('� Silence detected - auto-sending message...');
          // Check if we already sent this message
          if (messageSentRef.current) {
            console.log('Timer fired but message already sent - skipping');
            return;
          }
          
          console.log('Timer fired - sending message:', newQuery.substring(0, 50));
          setVoiceStartQuery(''); // Clear the starting query
          
          // Stop listening while sending
          setIsVoiceListening(false);
          
          // Mark as sent to prevent duplicates
          messageSentRef.current = true;
          
          // Send message first - PASS TALK MODE FLAG
          console.log('🎤 ChatInterface: Calling onSendMessage with talkMode=true (timer)');
          onSendMessage(null, false, newQuery, true);  // talkMode = true
          
          // Force clear immediately - multiple methods to ensure it works
          setQuery('');
          
          // Force clear via direct DOM manipulation as backup
          const textField = document.querySelector('input[type="text"], textarea');
          if (textField) {
            textField.value = '';
            // Trigger React's onChange to sync state
            const event = new Event('input', { bubbles: true });
            textField.dispatchEvent(event);
          }
          
          console.log('🧹 FORCED text field clear (timer)');
          
          // Additional backup clear
          setTimeout(() => {
            setQuery('');
            console.log('🧹 Backup text field clear (timer)');
          }, 0);
        }, 2000); // 2 seconds of silence
      }
      
      // If isFinal but not in hands-free mode, just update the query
      if (isFinal && !handsFreeModeActive) {
        console.log('✅ Final transcript received (manual mode)');
        setVoiceStartQuery('');
      }
    }
  };

  /**
   * Play audio from base64 data (from backend TTS integration)
   */
  const playAudioFromBase64 = async (audioData, format = 'mp3') => {
    try {
      console.log('🎤 Playing audio from backend integration:', {
        hasAudio: !!audioData,
        format,
        dataLength: audioData ? audioData.length : 0
      });
      
      setIsSpeaking(true);
      
      // Convert base64 to blob
      const audioBlob = new Blob(
        [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
        { type: `audio/${format}` }
      );
      
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      // Store reference for potential interruption
      currentAudioRef.current = audio;
      console.log('🎵 Storing audio reference for interruption support');
      
      // Wait for audio to finish
      await new Promise((resolve, reject) => {
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.onerror = (error) => {
          URL.revokeObjectURL(audioUrl);
          reject(error);
        };
        audio.play().catch(reject);
      });
      
      setIsSpeaking(false);
      
      console.log('✅ Backend TTS finished playing audio');
      // Note: Mic is already active for next input (activated when TTS started)
      
    } catch (error) {
      console.error('❌ Failed to play backend audio:', error);
      setIsSpeaking(false);
      
      // Fallback to mic reactivation even on error
      if (handsFreeModeActive && voiceMode) {
        setTimeout(() => {
          messageSentRef.current = false;
          setIsVoiceListening(true);
          console.log('🔄 Talk Mode: Reactivating mic after audio error');
        }, 500);
      }
    }
  };

  /**
   * Play AI response as speech and reactivate mic when done (Talk Mode)
   */
  const playAIResponse = async (text) => {
    console.log('🎙️ playAIResponse called', {
      voiceMode,
      handsFreeModeActive,
      loading,
      isSpeaking,
      isVoiceListening,
      hasText: !!text
    });
    
    if (!voiceMode || !text || text.trim() === '') {
      console.log('⚠️ Skipping playback:', { voiceMode, hasText: !!text });
      return;
    }
    
    try {
      // Extract plain text from markdown
      const plainText = text
        .replace(/```[\s\S]*?```/g, '') // Remove code blocks
        .replace(/`[^`]+`/g, '') // Remove inline code
        .replace(/[#*_\[\]()]/g, '') // Remove markdown symbols
        .replace(/\n{2,}/g, '. ') // Replace multiple newlines with period
        .trim();
      
      if (plainText.length === 0) {
        console.log('⚠️ No plain text after processing');
        return;
      }
      
      console.log('🎵 Adding text to speech queue (will call /tts/synthesize endpoint)...');
      console.log('Plain text to speak:', plainText.substring(0, 100) + '...');
      
      // Mark as speaking
      setIsSpeaking(true);
      
      // Add to speech queue - this calls /tts/synthesize endpoint
      console.log('📡 Calling TTS API via speechQueue.add()...');
      await speechQueue.add(plainText, {
        voiceName: 'en-US-Wavenet-D',
        speakingRate: 1.1,
        pitch: 0.0
      });
      
      console.log('✅ TTS finished playing audio');
      
      // Mark speaking as done
      setIsSpeaking(false);
      
      // If in talk mode (hands-free), reactivate mic to listen for next question
      if (handsFreeModeActive && voiceMode) {
        console.log('✅ CONDITIONS MET - Will restart mic in 500ms...');
        console.log('🔄 Talk Mode: Restarting mic to listen for next question...');
        // Wait a brief moment after TTS finishes before restarting mic
        setTimeout(() => {
          console.log('🎤 === TIMEOUT EXECUTED - RESTARTING MIC ===');
          console.log('🎤 First stopping any existing recognition...');
          
          // First, ensure mic is stopped
          setIsVoiceListening(false);
          
          // Then restart after a longer moment to ensure cleanup
          setTimeout(() => {
            console.log('🎤 Now restarting voice recognition...');
            messageSentRef.current = false; // Reset duplicate prevention flag
            setIsVoiceListening(true);
            console.log('🎤 Mic restarted - ready for next question');
          }, 300);
        }, 500);
      } else {
        console.log('❌ CONDITIONS NOT MET - NOT reactivating mic');
        console.log('⏸️ Reason:', { 
          handsFreeModeActive: handsFreeModeActive ? '✅' : '❌', 
          voiceMode: voiceMode ? '✅' : '❌',
          both: (handsFreeModeActive && voiceMode) ? '✅' : '❌'
        });
      }
    } catch (error) {
      console.error('❌ Voice playback error:', error);
      setIsSpeaking(false);
      
      // Even on error, reactivate mic if in talk mode
      if (handsFreeModeActive && voiceMode) {
        console.log('🔄 Error recovery: Reactivating mic...');
        setTimeout(() => {
          messageSentRef.current = false;
          setIsVoiceListening(true);
        }, 500);
      }
    }
  };

  /**
   * Toggle voice mode
   */
  const handleToggleVoiceMode = () => {
    const newVoiceMode = !voiceMode;
    setVoiceMode(newVoiceMode);
    analytics.chatTalkModeToggle({ enabled: newVoiceMode });
    
    if (newVoiceMode) {
      console.log('🎯 Entering talk mode, current conversation length:', conversation.length);
      
      // Initialize ref to current conversation length to prevent replaying old messages
      prevConversationLengthRef.current = conversation.length;
      
      // Enable hands-free mode when activating voice mode
      setHandsFreeModeActive(true);
      // Clear any existing query
      setQuery('');
      // Activate mic to start listening (with small delay for smooth transition)
      setTimeout(() => {
        console.log('🎤 Attempting to activate mic...');
        messageSentRef.current = false; // Reset duplicate prevention flag
        setIsVoiceListening(true);
      }, 300);
    } else {
      console.log('🛑 Exiting talk mode');
      // Clear silence timer
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      // Stop any active AI audio immediately when user disables talk mode
      interruptCurrentAudio('User disabled talk mode');
      setIsSpeaking(false);
      setHandsFreeModeActive(false);
      setIsVoiceListening(false);
      // Clear query when exiting voice mode
      setQuery('');
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const supportedTypes = [
        'application/pdf',
        'text/plain',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/tiff'
      ];
      
      if (!supportedTypes.includes(file.type)) {
        setFileUploadError('Unsupported file type. Please upload PDF, Word, Excel, or image files.');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setFileUploadError('File size must be less than 5MB.');
        return;
      }
      
      setSelectedFile(file);
      setFileUploadError(null);
      
      // Automatically upload the file
      await handleFileUpload(file);
    }
  };

  /**
   * Upload and process file
   */
  const handleFileUpload = async (file = selectedFile) => {
    if (!file) return;
    
    setFileUploadLoading(true);
    setFileUploadError(null);
    
    try {
      const result = await uploadDocumentTemporarily(file);
      
      // Store the uploaded file data for chat context
      setUploadedFileData(result);
      
      // Set the query with the actual file path for reliable file lookup
      setQuery(`📎 ${result.file_path}|${file.name}`);
      
      // Clear the selected file since it's now uploaded
      setSelectedFile(null);
      
    } catch (error) {
      setFileUploadError(error.message);
    } finally {
      setFileUploadLoading(false);
    }
  };

  /**
   * Handle sending message with potential file data
   */
  const handleSendMessage = () => {
    // Don't send if query is empty
    if (!query.trim()) return;
    
    if (uploadedFileData && query.includes('📎')) {
      // For files, send the message with file attachment
      onSendMessage();
      
      // Clear everything immediately after send
      setUploadedFileData(null);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      // Regular message sending
      onSendMessage();
    }
    
    // Force clear text field after send (parent should clear, but ensure it happens)
    setTimeout(() => {
      console.log('🧹 Clearing text field after regular send');
      setQuery('');
    }, 0);
  };

  /**
   * Remove selected file
   */
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileUploadError(null);
    setUploadedFileData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Clear the query if it contains file attachment
    if (query.includes('📎')) {
      setQuery('');
    }
  };

  /**
   * Remove uploaded file from chat interface
   */
  const handleRemoveUploadedFile = async () => {
    if (!uploadedFileData) return;

    try {
      // Call the chat service to delete the temporary file
      await deleteTemporaryDocument(uploadedFileData.file_path);
      
      // Clear the uploaded file data and query
      setUploadedFileData(null);
      setQuery('');
      setFileUploadError(null);

    } catch (error) {
      console.error('Error removing uploaded file:', error.message);
      // Continue with UI cleanup even if backend delete fails
      setUploadedFileData(null);
      setQuery('');
      setFileUploadError(null);
    }
  };

  /**
   * Handle voice listening state changes
   */
  const handleVoiceListeningChange = (listening) => {
    console.log('🎧 Voice listening changed:', listening, '(from InlineVoiceInput callback)');
    setIsVoiceListening(listening);
    if (listening) {
      // Save the current query when starting voice input
      setVoiceStartQuery(query);
    }
  };

  /**
   * Handle microphone permission errors
   */
  const handleMicPermissionError = (errorMessage) => {
    console.error('🚫 Mic permission error:', errorMessage);
    setMicPermissionError(errorMessage);
    showNotification(errorMessage, 'error');

    // Exit voice mode if permission is denied
    if (voiceMode) {
      setVoiceMode(false);
      setHandsFreeModeActive(false);
      setIsVoiceListening(false);
    }
  };

  /**
   * Handle voice recording state changes
   */
  const handleVoiceRecordingChange = (recording) => {
    setIsVoiceRecording(recording);
  };

  /**
   * Handle keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+Shift+V or Cmd+Shift+V to trigger voice input
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'V') {
        event.preventDefault();
        // Focus would trigger voice input button if we had a ref to it
        // For now, this provides the shortcut foundation
        console.log('Voice input shortcut triggered (Ctrl+Shift+V)');
      }
    };

    // Only add listener for fullscreen variant to avoid conflicts
    if (!isPanel) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isPanel]);

  /**
   * Monitor speech queue playing state
   */
  useEffect(() => {
    speechQueue.setOnPlayingChange((isPlaying) => {
      console.log('🔊 Speech playing state changed:', isPlaying);
      setIsSpeaking(isPlaying);
    });
    
    return () => {
      speechQueue.setOnPlayingChange(null);
    };
  }, []);

  /**
   * Debug: Monitor isVoiceListening state changes
   */
  useEffect(() => {
    // console.log('🎤 isVoiceListening state updated:', isVoiceListening, {
    //   voiceMode,
    //   handsFreeModeActive,
    //   isSpeaking,
    //   loading
    // });
  }, [isVoiceListening]);

  /**
   * Auto-play AI responses when voice mode is enabled
   */
  useEffect(() => {
    // console.log('🔍 Conversation changed:', {
    //   voiceMode,
    //   conversationLength: conversation?.length,
    //   prevLength: prevConversationLengthRef.current,
    //   lastMessageType: conversation?.[conversation?.length - 1]?.message_type,
    //   lastMessagePreview: conversation?.[conversation?.length - 1]?.text?.substring(0, 30)
    // });
    
    // Only process if voice mode is active and conversation has new messages
    if (!voiceMode || !conversation || conversation.length === 0) {
      // console.log('⏭️ Skipping TTS - voice mode disabled or no conversation');
      return;
    }
    
    // Check if conversation length increased OR if last message changed (streaming update)
    const hasNewMessage = conversation.length > prevConversationLengthRef.current;
    const currentLastMessage = conversation[conversation.length - 1];
    const prevMessageType = prevConversationLengthRef.lastMessageType;
    // A streamed answer becomes final without changing the conversation length, so the
    // streaming bubble counts as a placeholder just like the thinking one.
    const messageTypeChanged = currentLastMessage?.message_type !== prevMessageType &&
      (prevMessageType === CONVERSAYION_MESSAGE_TYPES.AI_THINKING || prevMessageType === CONVERSAYION_MESSAGE_TYPES.AI_STREAMING);
    
    console.log('📊 New message check:', {
      hasNewMessage,
      messageTypeChanged,
      currentLength: conversation.length,
      prevLength: prevConversationLengthRef.current,
      currentMessageType: currentLastMessage?.message_type,
      prevMessageType
    });
    
    prevConversationLengthRef.current = conversation.length;
    prevConversationLengthRef.lastMessageType = currentLastMessage?.message_type;
    
    // Process TTS if new message OR if message type changed from thinking to actual response
    if (!hasNewMessage && !messageTypeChanged) {
      console.log('⏭️ No new message and no type change - skipping TTS');
      return;
    }
    
    const lastMessage = currentLastMessage;
    console.log('📨 Last message FULL DETAILS:', {
      type: lastMessage?.type,
      message_type: lastMessage?.message_type,
      hasText: !!lastMessage?.text,
      textPreview: lastMessage?.text?.substring(0, 50),
      fullMessage: lastMessage
    });
    
    // Only play NEW AI messages (not user messages, not thinking indicators)
    // Check both type and message_type fields for AI messages
    const isAIThinkingMessage = lastMessage?.message_type === CONVERSAYION_MESSAGE_TYPES.AI_THINKING ||
                                lastMessage?.message_type === CONVERSAYION_MESSAGE_TYPES.AI_STREAMING;
    const isToolOrFunction = lastMessage?.message_type === CONVERSAYION_MESSAGE_TYPES.TOOL_EXECUTION || 
                             lastMessage?.message_type === CONVERSAYION_MESSAGE_TYPES.FUNCTION_EXECUTION;
    const isUserMessage = lastMessage?.type === 'user' || lastMessage?.message_type === CONVERSAYION_MESSAGE_TYPES.HUMAN;
    
    // Check if it's an AI message by either type='ai' OR message_type='ai' (or similar AI indicators)
    const isAIMessage = lastMessage?.type === 'ai' || 
                        lastMessage?.message_type === CONVERSAYION_MESSAGE_TYPES.AI ||
                        lastMessage?.message_type === 'general'; // 'general' is used for regular AI responses
    
    // Play if: it's an AI message, has text, not thinking, not tool/function execution, not user message
    const isActualAIResponse = isAIMessage && 
                               lastMessage?.text && 
                               !isAIThinkingMessage && 
                               !isToolOrFunction &&
                               !isUserMessage;
    
    console.log('🔍 TTS Decision:', {
      isAIMessage,
      isActualAIResponse,
      isUserMessage,
      isThinking: isAIThinkingMessage,
      isToolOrFunction,
      messageType: lastMessage?.message_type,
      type: lastMessage?.type
    });
    
    if (isActualAIResponse) {
      console.log('🎤 ✅ CALLING playAIResponse() to read response with TTS');
      console.log('Text to speak:', lastMessage.text.substring(0, 100) + '...');
      
      // Check if message includes audio_data (from backend TTS integration)
      if (lastMessage.audio_data) {
        console.log('🎤 Using audio from backend TTS integration');
        
        // 🎆 CRITICAL: Activate mic IMMEDIATELY when AI starts speaking for interruption detection
        if (handsFreeModeActive && voiceMode) {
          console.log('🟢 === ACTIVATING MIC DURING TTS FOR INTERRUPTION ===');
          console.log('🟢 Enabling voice listening while AI speaks...');
          messageSentRef.current = false; // Reset flag
          setIsVoiceListening(true); // Start listening for interruption
        }
        
        playAudioFromBase64(lastMessage.audio_data, lastMessage.audio_format || 'mp3');
      } else {
        console.log('🎤 Fallback to frontend TTS call');
        
        // 🎆 CRITICAL: Activate mic IMMEDIATELY for frontend TTS too
        if (handsFreeModeActive && voiceMode) {
          console.log('🟢 === ACTIVATING MIC DURING FRONTEND TTS FOR INTERRUPTION ===');
          messageSentRef.current = false;
          setIsVoiceListening(true);
        }
        
        playAIResponse(lastMessage.text);
      }
    } else {
      console.log('⏭️ ❌ Not playing TTS - reason:', {
        isUserMessage,
        noText: !lastMessage?.text,
        isThinking: isAIThinkingMessage,
        isToolOrFunction,
        notAIMessage: !isAIMessage,
        messageType: lastMessage?.message_type,
        type: lastMessage?.type
      });
    }
  }, [conversation, voiceMode]);
  
  // Styling based on variant
  const messageContainerSx = React.useMemo(() => (isPanel ? {
    width: '100%', // Use full width of container
    height: '100%', // Use full height
    pt: 1,
    pb: 2
  } : {
    width: '100%', // Use full width of container
    height: '100%', // Use full height
    pt: 2,
    pb: 2
  }), [isPanel]);

  const messageSx = React.useMemo(() => (isPanel ? {
    p: 1.5,
    maxWidth: '85%', // Smaller width for messages
    fontSize: '0.875rem'
  } : {
    p: 2,
    maxWidth: '75%' // Smaller width for messages
  }), [isPanel]);

  // iOS Safari zooms focused inputs with font-size < 16px.
  const panelInputFontSize = isPanel ? { xs: '16px', sm: '0.9rem' } : '1rem';

  const textFieldSx = isPanel ? {
    fontSize: '0.875rem',
    lineHeight: 2,
  } : {
    fontSize: '1rem',
    lineHeight: 1.5,
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', // Use 100% of parent height
      width: '100%',  // Use 100% of parent width
      overflow: 'hidden',           
    }}>
      {/* Messages Area - Scrollable with fade effect */}
      <MemoizedMessageList
        conversation={conversation}
        isPanel={isPanel}
        loading={loading}
        showLoadingIndicator={showLoadingIndicator}
        isAiThinking={isAiThinking}
        voiceMode={voiceMode}
        isSpeaking={isSpeaking}
        isVoiceListening={isVoiceListening}
        messagesEndRef={messagesEndRef}
        messageContainerSx={messageContainerSx}
        messageSx={messageSx}
        onSubmitFeedback={onSubmitFeedback}
        feedbackSubmittingId={feedbackSubmittingId}
      />

      {/* Input Area - Fixed at bottom */}
      <Box sx={{ 
        flexShrink: 0,
        borderTop: isPanel ? '0px solid' : 'none',
        borderTopColor: 'divider',
        pt: isPanel ? 1 : 0,
        pb: 1,
        px: isPanel ? 1 : 2, // Add horizontal padding
        width: '100%',
        maxWidth: isPanel ? '100%' : '1100px', // Constrain input width
        mx: 'auto' // Center the input area
      }}>
        <Paper
          elevation={0}
          sx={{
            p: isPanel ? 1 : 2,
            border: '1px solid',
            //borderColor: 'divider',
            borderColor: isPanel ? '#efefef' : 'divider',
            borderRadius: '.99rem',
            boxShadow: (theme) => theme.shadows[2]
          }}
        >
          {/* Token limit reached — show sign-up CTA instead of input */}
          {tokenLimitReached ? (
            <Box sx={{ textAlign: 'center', py: isPanel ? 1.5 : 2.5 }}>
              <Typography
                variant={isPanel ? 'body2' : 'body1'}
                sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
              >
                You've used your free messages
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: isPanel ? 1.5 : 2 }}
              >
                Create a free account to keep chatting with no limits.
              </Typography>
              <Button
                variant="gradient"
                size={isPanel ? 'small' : 'medium'}
                href="/signup"
                sx={{
                  px: isPanel ? 3 : 4,
                }}
              >
                Sign up for free
              </Button>
            </Box>
          ) : (
            <>
          {/* File Upload Error */}
          {fileUploadError && (
            <Alert severity="error" onClose={() => setFileUploadError(null)} sx={{ mb: 2 }}>
              {fileUploadError}
            </Alert>
          )}
          
          <Stack direction="row" alignItems="flex-end" sx={{ px: isPanel ? 0.5 : 0 }}>
            <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
              {/* Visible input field with file attachment support */}
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-start', // Align to top for multiline
                  minHeight: 'auto', // Let it size naturally
                  width: '100%'
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  value={query}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setQuery(newValue);
                  }}
                  onInput={(e) => {
                    // Exit Talk Mode on any manual typing (optimized for performance)
                    if (voiceMode) {
                      if (!isVoiceListening && !isSpeaking) {
                        // console.log('🔤 Manual typing detected - exiting Talk Mode');
                        setVoiceMode(false);
                        setHandsFreeModeActive(false);
                        setVoiceStartQuery('');
                      }
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder={voiceMode && isVoiceListening ? "Listening... Speak now" : placeholder}
                  variant="standard"
                  disabled={voiceMode && !isVoiceListening && isSpeaking}
                  InputProps={{
                    startAdornment: voiceMode && isVoiceListening ? (
                      <Box
                        sx={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          bgcolor: 'error.main',
                          animation: `${pulse} 1.5s ease-in-out infinite`,
                          mr: 1,
                          flexShrink: 0
                        }}
                      />
                    ) : null,
                    disableUnderline: true,
                    sx: {
                      fontSize: panelInputFontSize,
                      lineHeight: 1.4, // Slightly tighter line height for better fit
                      padding: isPanel ? '12px 1px' : '12px 12px',
                      paddingRight: isPanel ? '10px' : '20px', // Less space for buttons in panel
                      '& .MuiInputBase-input': {
                        padding: 0,
                        fontSize: panelInputFontSize,
                        lineHeight: 1.4,
                        resize: 'none',
                        '&::placeholder': {
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          opacity: 1,
                          fontSize: panelInputFontSize
                        }
                      }
                    },
                    // Custom rendering for file attachments
                    inputComponent: query.includes('📎') ? 'div' : undefined,
                    inputProps: query.includes('📎') ? {
                      children: (
                        <TextWithAttachments 
                          text={query} 
                          isUser={true} 
                          withSparkle={false}
                          isPanel={isPanel}
                          isInputField={true}
                          onRemoveFile={handleRemoveUploadedFile}
                        />
                      ),
                      style: {
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        width: '100%',
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: 1.4,
                        resize: 'none'
                      }
                    } : {
                      // No more than KeyTa takes: a longer paste is cut here instead of refused.
                      maxLength: CHAT_QUERY_MAX_CHARS,
                    }
                  }}
                  sx={{
                    '& .MuiInput-root': {
                      border: 'none',
                      '&:before': { display: 'none' },
                      '&:after': { display: 'none' },
                      '&:hover:not(.Mui-disabled):before': { display: 'none' }
                    }
                  }}
                />
              </Box>
            </Box>
            
            {/* Fixed position buttons container */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: isPanel ? 0.25 : 0.5,
              gap: isPanel ? 0.5 : 0.5,
              height: '48px'
            }}>
              {/* Voice Input - Only shown in hands-free mode (hidden from UI, controlled programmatically) */}
              <Box sx={{ display: 'none' }}>
                <InlineVoiceInput
                  onTranscript={handleVoiceTranscript}
                  onListeningChange={handleVoiceListeningChange}
                  onRecordingStateChange={handleVoiceRecordingChange}
                  onPermissionError={handleMicPermissionError}
                  onSpeechStart={(text) => {
                    console.log('🎯 === onSpeechStart CALLBACK TRIGGERED ===');
                    console.log('🎯 Text:', text.substring(0, 50));
                    console.log('🎯 Current audio state:', {
                      isSpeaking,
                      speechQueuePlaying: speechQueue.getIsPlaying(),
                      hasCurrentAudio: !!currentAudioRef.current
                    });
                    interruptCurrentAudio('User started speaking - onSpeechStart callback');
                    console.log('🎯 === onSpeechStart CALLBACK COMPLETE ===');
                  }} // 🆕 NEW: Interrupt audio when speech starts
                  disabled={loading}
                  size="small"
                  silenceTimeout={handsFreeModeActive ? 2000 : 5000}
                  autoStart={isVoiceListening}
                  analyticsSource="chat_talk_mode"
                />
              </Box>

              {/* File Upload Button */}
              {!voiceMode && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.tiff"
                    style={{ display: 'none' }}
                  />
                  <Tooltip title="Upload Document">
                    <span>
                      <IconButton
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading || fileUploadLoading}
                        size="small"
                        sx={{
                          color: fileUploadLoading ? 'primary.main' : 'text.secondary',
                          width: '32px',
                          height: '32px',
                          padding: '4px',
                          '&:hover': { 
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        {fileUploadLoading ? 
                          <CircularProgress size={16} /> : 
                          <AttachFileIcon fontSize="small" />
                        }
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}

              {/* Stacked Voice Mode Toggle Button and Send Button */}
              <Box sx={{ position: 'relative', width: '40px', height: '40px' }}>
                {/* Voice Mode Toggle Button - Show when voice mode is active OR when text field is empty */}
                {(voiceMode || !query.trim()) && (
                  <Tooltip 
                    title={
                      micPermissionError 
                        ? "Microphone access denied. Enable in browser settings." 
                        : voiceMode 
                          ? "Exit Talk Mode" 
                          : "Enter Talk Mode (Hands-Free)"
                    }
                    placement="top"
                    arrow
                  >
                    <span style={{ position: 'absolute', top: 0, left: 0, display: 'inline-flex' }}>
                      <IconButton
                        onClick={handleToggleVoiceMode}
                        disabled={loading || !!micPermissionError}
                        size="medium"
                        sx={{
                          color: micPermissionError
                            ? 'action.disabled'
                            : voiceMode
                              ? 'error.main'
                              : 'primary.main',
                          bgcolor: micPermissionError
                            ? (theme) => alpha(theme.palette.text.primary, 0.05)
                            : voiceMode
                              ? (theme) => alpha(theme.palette.error.main, 0.1)
                              : (theme) => alpha(theme.palette.primary.main, 0.1),
                          width: '40px',
                          height: '40px',
                          zIndex: 1,
                          cursor: loading || !!micPermissionError ? 'not-allowed' : 'pointer',
                          '&:hover': {
                            bgcolor: micPermissionError
                              ? (theme) => alpha(theme.palette.text.primary, 0.05)
                              : voiceMode
                                ? (theme) => alpha(theme.palette.error.main, 0.2)
                                : (theme) => alpha(theme.palette.primary.main, 0.2),
                          },
                          '&.Mui-disabled': {
                            color: 'action.disabled',
                            pointerEvents: 'none'
                          }
                        }}
                      >
                        <WaveformIcon isActive={voiceMode} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                
                {/* Send Button - Show when text field has content AND not in voice mode */}
                {query.trim() && !voiceMode && (
                  <Tooltip title="Send Message" placement="top" arrow>
                    <span style={{ position: 'absolute', top: 0, left: 0, display: 'inline-flex' }}>
                      <IconButton
                        onClick={handleSendMessage}
                        disabled={loading}
                        size="medium"
                        sx={{
                          color: loading ? (theme) => alpha(theme.palette.text.primary, 0.3) : 'primary.main',
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          width: '40px',
                          height: '40px',
                          zIndex: 1,
                          cursor: loading ? 'not-allowed' : 'pointer',
                          '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
                          },
                          '&.Mui-disabled': {
                            color: 'action.disabled',
                            pointerEvents: 'none'
                          }
                        }}
                      >
                        <SendIcon fontSize="medium" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Stack>
            </>
          )}
        </Paper>
        
        {!isPanel && (
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              textAlign: 'center', 
              mt: 2, 
              color: 'text.secondary' 
            }}
          >
            {/* AI-powered assistant. May produce inaccurate information about properties. */}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChatInterface;
